import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import AppLayout from '../components/AppLayout';
import RoleGate from '../components/RoleGate';
import { ROLES } from '../lib/auth/roles';
import { Icons } from '../lib/Icons';
import VideoThumbnail from '../components/VideoThumbnail';
import {
  getAllActivitiesWithResources,
  getActivitiesMissingResources,
  bulkCheckResources,
  bulkSearchAllActivities,
  getPendingVideoAssignments,
  acceptVideoCandidate,
  bulkSearchProductsForAllActivities,
  getPendingResourceAssignments,
  acceptProductCandidate,
} from '../api/adminResources';

const AdminResourcesDashboard = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'missing', 'broken', 'needs-review'
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('activities'); // 'activities', 'pending'
  const [bulkSearching, setBulkSearching] = useState(false);
  const [bulkSearchProgress, setBulkSearchProgress] = useState(null);
  const [pendingAssignments, setPendingAssignments] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [previewVideo, setPreviewVideo] = useState(null);
  const [accepting, setAccepting] = useState(null);
  
  // Product search state
  const [bulkSearchingProducts, setBulkSearchingProducts] = useState(false);
  const [bulkSearchProductsProgress, setBulkSearchProductsProgress] = useState(null);
  const [pendingProductAssignments, setPendingProductAssignments] = useState([]);
  const [loadingPendingProducts, setLoadingPendingProducts] = useState(false);
  const [acceptingProduct, setAcceptingProduct] = useState(null);
  
  // Bulk search options
  const [searchOptions, setSearchOptions] = useState({
    skipWithPendingAssignments: false, // Include activities with pending by default for better auto-accept yield
    includeBrokenOnly: false, // Only search for activities with broken videos (not missing)
    includeActivitiesWithFewerThanVideos: 1, // 1 = only no videos, 2 = no or one video
  });

  useEffect(() => {
    loadActivities();
    loadPendingAssignments(); // Load for bulk search warning when skip is on
  }, []);

  useEffect(() => {
    if (activeTab === 'pending') {
      loadPendingAssignments();
      loadPendingProductAssignments();
    }
  }, [activeTab]);

  const loadActivities = async () => {
    try {
      setLoading(true);
      const data = await getAllActivitiesWithResources();
      setActivities(data.activities || []);
    } catch (error) {
      console.error('Error loading activities:', error);
      toast.error('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingAssignments = async () => {
    try {
      setLoadingPending(true);
      const data = await getPendingVideoAssignments();
      setPendingAssignments(data.assignments || []);
    } catch (error) {
      console.error('Error loading pending assignments:', error);
      toast.error('Failed to load pending assignments');
    } finally {
      setLoadingPending(false);
    }
  };

  const handleBulkSearch = async () => {
    const searchType = searchOptions.includeBrokenOnly ? 'activities with broken videos' : 'activities missing videos';
    const fewVideosNote = searchOptions.includeActivitiesWithFewerThanVideos > 1
      ? ` (including activities with fewer than ${searchOptions.includeActivitiesWithFewerThanVideos} videos)`
      : '';
    const skipNote = searchOptions.skipWithPendingAssignments ? ' (skipping those with pending assignments)' : '';
    
    if (!window.confirm(`This will search YouTube for ${searchType}${fewVideosNote}${skipNote}. This may use significant API quota. Continue?`)) {
      return;
    }

    try {
      setBulkSearching(true);
      
      // Process in batches
      let offset = 0;
      const limit = 20;
      let totalProcessed = 0;
      let totalWithCandidates = 0;
      let totalAutoAccepted = 0;
      let hasMore = true;
      let totalMissing = 0;
      let lastResult = null;

      while (hasMore) {
        setBulkSearchProgress(`Processing activities ${offset + 1} - ${offset + limit}...`);

        const result = await bulkSearchAllActivities({
          limit,
          offset,
          skipWithPendingAssignments: searchOptions.skipWithPendingAssignments,
          includeBrokenOnly: searchOptions.includeBrokenOnly,
          includeActivitiesWithFewerThanVideos: searchOptions.includeActivitiesWithFewerThanVideos,
        });

        lastResult = result;
        totalProcessed += result.totalActivities;
        totalWithCandidates += result.activitiesWithCandidates;
        totalAutoAccepted += result.autoAcceptedCount || 0;
        totalMissing = result.totalMissingVideos || totalMissing;

        // Check pagination
        if (result.pagination) {
          hasMore = result.pagination.hasMore;
          offset += result.pagination.processed;
          setBulkSearchProgress(`Processed ${offset} of ${totalMissing} activities...`);
        } else {
          hasMore = false;
        }
        
        // Small delay between batches
        if (hasMore) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      setBulkSearchProgress(null);
      const skippedDueToPending = lastResult?.skippedDueToPending ?? 0;
      if (totalProcessed === 0 && totalMissing === 0) {
        toast(lastResult?.message || 'No activities need videos. All activities already have at least one video.');
      } else {
        const skippedNote = skippedDueToPending > 0 ? `, skipped ${skippedDueToPending} (had pending)` : '';
        toast.success(`Completed! Processed ${totalProcessed} activities, found ${totalWithCandidates} with candidates, auto-accepted ${totalAutoAccepted} videos${skippedNote}`);
      }
      // Switch to pending tab to show results
      setActiveTab('pending');
      await loadPendingAssignments();
    } catch (error) {
      console.error('Error in bulk search:', error);
      toast.error('Failed to complete bulk search');
      setBulkSearchProgress(null);
    } finally {
      setBulkSearching(false);
    }
  };

  const handleAcceptCandidate = async (assignment, candidate) => {
    try {
      setAccepting(`${assignment.activitySlug}-${candidate.videoId}`);
      await acceptVideoCandidate({
        activitySlug: assignment.activitySlug,
        candidate,
      });
      toast.success('Video added to activity');
      await loadPendingAssignments();
      await loadActivities();
    } catch (error) {
      console.error('Error accepting candidate:', error);
      toast.error('Failed to add video');
    } finally {
      setAccepting(null);
    }
  };

  // Product search functions
  const loadPendingProductAssignments = async () => {
    try {
      setLoadingPendingProducts(true);
      const data = await getPendingResourceAssignments();
      setPendingProductAssignments(data.assignments || []);
    } catch (error) {
      console.error('Error loading pending product assignments:', error);
      // Don't show error toast, just silently fail - might not be configured yet
    } finally {
      setLoadingPendingProducts(false);
    }
  };

  const handleBulkSearchProducts = async () => {
    if (!window.confirm('This will search Amazon for products for all activities missing buy resources. This uses AI and Amazon API quota. Continue?')) {
      return;
    }

    try {
      setBulkSearchingProducts(true);
      
      // Process in batches
      let offset = 0;
      const limit = 20;
      let totalProcessed = 0;
      let totalProductsFound = 0;
      let hasMore = true;
      let totalMissing = 0;

      while (hasMore) {
        setBulkSearchProductsProgress(`Processing activities ${offset + 1} - ${offset + limit}...`);
        
        const result = await bulkSearchProductsForAllActivities({ limit, offset });
        
        totalProcessed += result.processed;
        totalProductsFound += result.productsFound;
        totalMissing = result.totalMissingProducts || totalMissing;
        
        // Check pagination
        if (result.pagination) {
          hasMore = result.pagination.hasMore;
          offset += result.pagination.processed;
          setBulkSearchProductsProgress(`Processed ${offset} of ${totalMissing} activities...`);
        } else {
          hasMore = false;
        }
        
        // Small delay between batches
        if (hasMore) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      setBulkSearchProductsProgress(null);
      toast.success(`Found ${totalProductsFound} products for ${totalProcessed} activities`);
      setActiveTab('pending');
      await loadPendingProductAssignments();
    } catch (error) {
      console.error('Error in bulk product search:', error);
      if (error.message?.includes('not configured')) {
        toast.error('Amazon API not configured. Please set up Amazon PA-API credentials.');
      } else {
        toast.error('Failed to complete product search');
      }
      setBulkSearchProductsProgress(null);
    } finally {
      setBulkSearchingProducts(false);
    }
  };

  const handleAcceptProductCandidate = async (assignment, product) => {
    try {
      setAcceptingProduct(`${assignment.activityId}-${product.asin}`);
      await acceptProductCandidate({
        activitySlug: assignment.activityId,
        product,
      });
      toast.success('Product added to activity');
      await loadPendingProductAssignments();
      await loadActivities();
    } catch (error) {
      console.error('Error accepting product:', error);
      toast.error('Failed to add product');
    } finally {
      setAcceptingProduct(null);
    }
  };

  const handleBulkCheck = async () => {
    if (!window.confirm('This will check all resources. This may take several minutes. Continue?')) {
      return;
    }

    try {
      setChecking(true);
      await bulkCheckResources();
      toast.success('Bulk check started. Results will update automatically.');
      // Reload after a delay
      setTimeout(() => {
        loadActivities();
      }, 5000);
    } catch (error) {
      console.error('Error starting bulk check:', error);
      toast.error('Failed to start bulk check');
    } finally {
      setChecking(false);
    }
  };

  const getFilteredActivities = () => {
    let filtered = activities;

    // Apply filter
    switch (filter) {
      case 'missing':
        filtered = filtered.filter(
          (a) => a.resourceSummary.totalVideos === 0 || a.resourceSummary.unavailableVideos === a.resourceSummary.totalVideos
        );
        break;
      case 'broken-videos':
        filtered = filtered.filter(
          (a) => a.resourceSummary.unavailableVideos > 0
        );
        break;
      case 'broken-resources':
        filtered = filtered.filter(
          (a) => (a.resourceSummary.unavailableResources || 0) > 0
        );
        break;
      case 'broken-all':
        filtered = filtered.filter(
          (a) => a.resourceSummary.unavailableVideos > 0 || (a.resourceSummary.unavailableResources || 0) > 0
        );
        break;
      case 'needs-review':
        filtered = filtered.filter((a) => a.resourceSummary.needsReview > 0);
        break;
      case 'auto-accepted':
        filtered = filtered.filter((a) => {
          const resources = a.resources || [];
          return resources.some(r => r.status?.autoAccepted === true);
        });
        break;
      default:
        break;
    }

    // Apply search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (a) =>
          a.title.toLowerCase().includes(term) ||
          a.slug.toLowerCase().includes(term)
      );
    }

    return filtered;
  };

  const getStatistics = () => {
    const total = activities.length;
    const totalVideos = activities.reduce((sum, a) => sum + a.resourceSummary.totalVideos, 0);
    const totalResources = activities.reduce((sum, a) => sum + a.resourceSummary.totalResources, 0);
    const totalNonVideoResources = activities.reduce((sum, a) => sum + (a.resourceSummary.totalNonVideoResources || 0), 0);
    const unavailableVideos = activities.reduce((sum, a) => sum + a.resourceSummary.unavailableVideos, 0);
    const unavailableResources = activities.reduce((sum, a) => sum + (a.resourceSummary.unavailableResources || 0), 0);
    const needsReview = activities.reduce((sum, a) => sum + a.resourceSummary.needsReview, 0);
    const missingVideos = activities.filter((a) => a.resourceSummary.totalVideos === 0).length;
    
    // Count auto-accepted videos
    const autoAcceptedVideos = activities.reduce((sum, a) => {
      const resources = a.resources || [];
      return sum + resources.filter(r => r.status?.autoAccepted === true).length;
    }, 0);

    return {
      total,
      totalVideos,
      totalResources,
      totalNonVideoResources,
      unavailableVideos,
      unavailableResources,
      needsReview,
      missingVideos,
      autoAcceptedVideos,
    };
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Never';
    
    let date;
    
    // Handle Firestore Timestamp objects (with toDate method)
    if (typeof timestamp.toDate === 'function') {
      date = timestamp.toDate();
    }
    // Handle serialized Firestore timestamps from Cloud Functions
    else if (timestamp._seconds !== undefined) {
      date = new Date(timestamp._seconds * 1000);
    }
    else if (timestamp.seconds !== undefined) {
      date = new Date(timestamp.seconds * 1000);
    }
    // Handle ISO string dates
    else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    }
    // Handle Unix timestamps (milliseconds)
    else if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    }
    // Handle Date objects
    else if (timestamp instanceof Date) {
      date = timestamp;
    }
    else {
      return 'Invalid date';
    }
    
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusBadge = (activity) => {
    const { resourceSummary } = activity;
    const badges = [];
    
    // Check for broken resource links (non-video)
    const unavailableResources = resourceSummary.unavailableResources || 0;
    if (unavailableResources > 0) {
      badges.push(
        <span key="broken-links" className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          <Icons.Link className="w-3.5 h-3.5 inline-block mr-1" />
          {unavailableResources} Broken Link{unavailableResources > 1 ? 's' : ''}
        </span>
      );
    }
    
    // Check for no videos
    if (resourceSummary.totalVideos === 0) {
      badges.push(
        <span key="no-videos" className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          No Videos
        </span>
      );
    } else if (resourceSummary.unavailableVideos === resourceSummary.totalVideos) {
      badges.push(
        <span key="all-broken" className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <Icons.Film className="w-4 h-4 mr-1 inline-block" /> All Videos Broken
        </span>
      );
    } else if (resourceSummary.unavailableVideos > 0) {
      badges.push(
        <span key="some-broken" className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Icons.Film className="w-4 h-4 mr-1 inline-block" /> {resourceSummary.unavailableVideos} Broken
        </span>
      );
    }
    
    // Check for needs review
    if (resourceSummary.needsReview > 0) {
      badges.push(
        <span key="review" className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          Needs Review
        </span>
      );
    }
    
    // If no issues, show OK
    if (badges.length === 0) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><Icons.Check className="w-3 h-3 mr-1" /> OK</span>;
    }
    
    return <div className="flex flex-wrap gap-1">{badges}</div>;
  };

  const stats = getStatistics();
  const filteredActivities = getFilteredActivities();

  return (
    <RoleGate allow={[ROLES.SUPER_ADMIN]} fallback={<div>Access denied. Super admin access required.</div>}>
      <AppLayout>
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          <div className="mb-6">
            <h1 className="text-3xl font-bold mb-2">Resource Management</h1>
            <p className="text-gray-600">Monitor and manage videos and resources for all KB activities</p>
          </div>

          {/* Statistics - Clickable Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
            <div 
              onClick={() => setFilter('all')}
              className={`bg-white rounded-lg shadow p-4 cursor-pointer transition-all hover:shadow-md hover:scale-105 ${filter === 'all' ? 'ring-2 ring-blue-500' : ''}`}
            >
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-sm text-gray-600">Total Activities</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-2xl font-bold text-gray-900">{stats.totalVideos}</div>
              <div className="text-sm text-gray-600">Total Videos</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-2xl font-bold text-gray-900">{stats.totalNonVideoResources}</div>
              <div className="text-sm text-gray-600">Resource Links</div>
            </div>
            <div 
              onClick={() => setFilter('broken-videos')}
              className={`bg-white rounded-lg shadow p-4 border-l-4 border-red-500 cursor-pointer transition-all hover:shadow-md hover:scale-105 ${filter === 'broken-videos' ? 'ring-2 ring-red-500' : ''}`}
            >
              <div className="text-2xl font-bold text-red-600">{stats.unavailableVideos}</div>
              <div className="text-sm text-gray-600">Broken Videos</div>
            </div>
            <div 
              onClick={() => setFilter('broken-resources')}
              className={`bg-white rounded-lg shadow p-4 border-l-4 border-orange-500 cursor-pointer transition-all hover:shadow-md hover:scale-105 ${filter === 'broken-resources' ? 'ring-2 ring-orange-500' : ''}`}
            >
              <div className="text-2xl font-bold text-orange-600">{stats.unavailableResources}</div>
              <div className="text-sm text-gray-600">Broken Links</div>
            </div>
            <div 
              onClick={() => setFilter('auto-accepted')}
              className={`bg-white rounded-lg shadow p-4 border-l-4 border-emerald-500 cursor-pointer transition-all hover:shadow-md hover:scale-105 ${filter === 'auto-accepted' ? 'ring-2 ring-emerald-500' : ''}`}
            >
              <div className="text-2xl font-bold text-emerald-600">{stats.autoAcceptedVideos}</div>
              <div className="text-sm text-gray-600">Auto-Accepted</div>
            </div>
            <div 
              onClick={() => setFilter('needs-review')}
              className={`bg-white rounded-lg shadow p-4 cursor-pointer transition-all hover:shadow-md hover:scale-105 ${filter === 'needs-review' ? 'ring-2 ring-yellow-500' : ''}`}
            >
              <div className="text-2xl font-bold text-yellow-600">{stats.needsReview}</div>
              <div className="text-sm text-gray-600">Need Review</div>
            </div>
            <div 
              onClick={() => setFilter('missing')}
              className={`bg-white rounded-lg shadow p-4 cursor-pointer transition-all hover:shadow-md hover:scale-105 ${filter === 'missing' ? 'ring-2 ring-red-500' : ''}`}
            >
              <div className="text-2xl font-bold text-red-600">{stats.missingVideos}</div>
              <div className="text-sm text-gray-600">Missing Videos</div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="flex space-x-8">
              <button
                onClick={() => setActiveTab('activities')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'activities'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Activities ({activities.length})
              </button>
              <button
                onClick={() => setActiveTab('pending')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'pending'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Pending Assignments ({pendingAssignments.length})
              </button>
            </nav>
          </div>

          {/* Filters and Actions */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex flex-col gap-4">
              {/* Search and filter row */}
              <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                <div className="flex flex-col md:flex-row gap-4 flex-1">
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="Search activities..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="all">All Activities</option>
                    <option value="missing">Missing Videos</option>
                    <option value="broken-all">All Broken (Videos + Links)</option>
                    <option value="broken-videos">Broken Videos Only</option>
                    <option value="broken-resources">Broken Links Only</option>
                    <option value="needs-review">Needs Review</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleBulkCheck}
                    disabled={checking}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {checking ? 'Checking...' : 'Check All Resources'}
                  </button>
                  <button
                    onClick={loadActivities}
                    className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                  >
                    Refresh
                  </button>
                </div>
              </div>
              
              {/* Bulk search options and buttons */}
              <div className="border-t pt-4">
                <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                  {/* Search options */}
                  <div className="flex flex-wrap gap-4 items-center text-sm">
                    <span className="font-medium text-gray-700">Bulk Search Options:</span>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={searchOptions.skipWithPendingAssignments}
                        onChange={(e) => setSearchOptions(prev => ({ ...prev, skipWithPendingAssignments: e.target.checked }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-gray-600">Skip activities with pending assignments</span>
                    </label>
                    {searchOptions.skipWithPendingAssignments && pendingAssignments.length > 0 && (
                      <span className="text-amber-600 text-xs">
                        Uncheck to include {pendingAssignments.length} activities that currently have pending video assignments.
                      </span>
                    )}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={searchOptions.includeBrokenOnly}
                        onChange={(e) => setSearchOptions(prev => ({ ...prev, includeBrokenOnly: e.target.checked }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-gray-600">Only broken videos (not missing)</span>
                    </label>
                    {!searchOptions.includeBrokenOnly && (
                      <label className="flex items-center gap-2">
                        <span className="text-gray-600">Include activities with fewer than</span>
                        <select
                          value={searchOptions.includeActivitiesWithFewerThanVideos}
                          onChange={(e) => setSearchOptions(prev => ({ ...prev, includeActivitiesWithFewerThanVideos: Number(e.target.value) }))}
                          className="rounded border-gray-300 text-sm"
                        >
                          <option value={1}>1 video (only missing)</option>
                          <option value={2}>2 videos</option>
                          <option value={3}>3 videos</option>
                        </select>
                        <span className="text-gray-600">videos</span>
                      </label>
                    )}
                  </div>

                  {/* Bulk search buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={handleBulkSearch}
                      disabled={bulkSearching}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                    >
                      {bulkSearching ? (bulkSearchProgress || 'Searching...') : <><Icons.MagnifyingGlass className="w-4 h-4 inline-block mr-1" /> Bulk Search YouTube</>}
                    </button>
                    <button
                      onClick={handleBulkSearchProducts}
                      disabled={bulkSearchingProducts}
                      className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50"
                    >
                      {bulkSearchingProducts ? (bulkSearchProductsProgress || 'Searching...') : <><Icons.ShoppingCart className="w-4 h-4 mr-2 inline-block" /> Bulk Search Products</>}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Activities Tab Content */}
          {activeTab === 'activities' && (
            <>
              {loading ? (
                <div className="text-center py-12">
                  <div className="text-lg">Loading activities...</div>
                </div>
              ) : filteredActivities.length === 0 ? (
                <div className="bg-white rounded-lg shadow-md p-12 text-center">
                  <div className="text-gray-500">No activities found</div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Activity
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Videos
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Resource Links
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Checked
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredActivities.map((activity) => (
                          <tr key={activity.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{activity.title}</div>
                              <div className="text-sm text-gray-500">{activity.slug}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {getStatusBadge(activity)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {activity.resourceSummary.availableVideos > 0 && (
                                <span className="text-green-600 flex items-center">{activity.resourceSummary.availableVideos} <Icons.Check className="w-4 h-4 ml-0.5" /></span>
                              )}
                              {activity.resourceSummary.unavailableVideos > 0 && (
                                <span className="text-red-600 ml-2 flex items-center">{activity.resourceSummary.unavailableVideos} <Icons.XMark className="w-4 h-4 ml-0.5" /></span>
                              )}
                              {activity.resourceSummary.totalVideos === 0 && (
                                <span className="text-gray-400">0</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div className="flex flex-col">
                                <span>{activity.resourceSummary.totalNonVideoResources || 0} total</span>
                                {(activity.resourceSummary.unavailableResources || 0) > 0 && (
                                  <span className="text-orange-600 font-medium">
                                    <Icons.ExclamationTriangle className="w-3.5 h-3.5 inline-block mr-1" />
                                    {activity.resourceSummary.unavailableResources} broken
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(activity.resourceSummary.lastChecked)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button
                                onClick={() => navigate(`/admin/activities/${activity.slug}/edit`)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                Edit
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <div className="text-sm text-gray-600">
                      Showing {filteredActivities.length} of {activities.length} activities
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Pending Assignments Tab Content */}
          {activeTab === 'pending' && (
            <>
              {loadingPending ? (
                <div className="text-center py-12">
                  <div className="text-lg">Loading pending assignments...</div>
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Pending Video Assignments */}
                  {pendingAssignments.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-md p-8 text-center">
                      <div className="text-gray-500 mb-2 flex items-center justify-center"><Icons.Film className="w-5 h-5 mr-2" /> No pending video assignments</div>
                      <p className="text-sm text-gray-400">
                        Click "Bulk Search YouTube" to find videos for activities missing videos.
                      </p>
                    </div>
                  ) : (
                    <div>
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <Icons.Film className="w-5 h-5" /> Pending Video Assignments ({pendingAssignments.length})
                      </h3>
                      <div className="space-y-6">
                        {pendingAssignments.map((assignment) => (
                          <div key={assignment.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                            <div className="px-6 py-4 bg-gray-50 border-b">
                              <div className="flex justify-between items-center">
                                <div>
                                  <h3 className="font-semibold text-lg">{assignment.activityTitle}</h3>
                                  <p className="text-sm text-gray-500">{assignment.activitySlug}</p>
                                </div>
                                <button
                                  onClick={() => navigate(`/admin/activities/${assignment.activitySlug}/edit`)}
                                  className="text-blue-600 hover:text-blue-900 text-sm"
                                >
                                  Edit Activity →
                                </button>
                              </div>
                            </div>
                            <div className="p-6">
                              <h4 className="text-sm font-medium text-gray-700 mb-3">
                                Video Candidates ({assignment.candidates?.length || 0})
                              </h4>
                              <div className="space-y-4">
                                {assignment.candidates?.map((candidate, idx) => (
                                  <div
                                    key={candidate.videoId}
                                    className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                                  >
                                    <div className="flex gap-4">
                                      <div className="relative cursor-pointer" onClick={() => setPreviewVideo(candidate)}>
                                        <VideoThumbnail
                                          src={candidate.thumbnail}
                                          videoId={candidate.videoId}
                                          alt={candidate.title}
                                          className="w-32 h-20 object-cover rounded"
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded">
                                          <span className="text-white text-2xl">▶</span>
                                        </div>
                                      </div>
                                      <div className="flex-1">
                                        <h5 className="font-medium line-clamp-1">{candidate.title}</h5>
                                        <p className="text-sm text-gray-600">{candidate.channel}</p>
                                        <div className="flex items-center gap-2 mt-2">
                                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                            candidate.appropriatenessScore >= 70 ? 'bg-green-100 text-green-800' :
                                            candidate.appropriatenessScore >= 40 ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-red-100 text-red-800'
                                          }`}>
                                            Score: {candidate.appropriatenessScore}/100
                                          </span>
                                          <a
                                            href={candidate.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 text-xs hover:underline"
                                          >
                                            Open in YouTube ↗
                                          </a>
                                        </div>
                                      </div>
                                      <div className="flex flex-col gap-2">
                                        <button
                                          onClick={() => setPreviewVideo(candidate)}
                                          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
                                        >
                                          Preview
                                        </button>
                                        <button
                                          onClick={() => handleAcceptCandidate(assignment, candidate)}
                                          disabled={accepting === `${assignment.activitySlug}-${candidate.videoId}`}
                                          className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                                        >
                                          {accepting === `${assignment.activitySlug}-${candidate.videoId}` ? 'Adding...' : 'Accept'}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Pending Product Assignments */}
                  {loadingPendingProducts ? (
                    <div className="text-center py-8">
                      <div className="text-lg text-gray-500">Loading product suggestions...</div>
                    </div>
                  ) : pendingProductAssignments.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-md p-8 text-center">
                      <div className="text-gray-500 mb-2 flex items-center justify-center"><Icons.ShoppingCart className="w-5 h-5 mr-2" /> No pending product suggestions</div>
                      <p className="text-sm text-gray-400">
                        Click "Bulk Search Products" to find products for activities missing buy resources.
                      </p>
                    </div>
                  ) : (
                    <div>
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <Icons.ShoppingCart className="w-5 h-5" /> Pending Product Suggestions ({pendingProductAssignments.length})
                      </h3>
                      <div className="space-y-6">
                        {pendingProductAssignments.map((assignment) => (
                          <div key={assignment.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                            <div className="px-6 py-4 bg-orange-50 border-b">
                              <div className="flex justify-between items-center">
                                <div>
                                  <h3 className="font-semibold text-lg">{assignment.activityTitle}</h3>
                                  <p className="text-sm text-gray-500">{assignment.activityId}</p>
                                  {assignment.aiReasoning && (
                                    <p className="text-sm text-gray-600 mt-1 italic">
                                      <Icons.LightBulb className="w-4 h-4 inline-block mr-1" />
                                      {assignment.aiReasoning}
                                    </p>
                                  )}
                                </div>
                                <button
                                  onClick={() => navigate(`/admin/activities/${assignment.activityId}/edit`)}
                                  className="text-blue-600 hover:text-blue-900 text-sm"
                                >
                                  Edit Activity →
                                </button>
                              </div>
                            </div>
                            <div className="p-6">
                              <h4 className="text-sm font-medium text-gray-700 mb-3">
                                Product Candidates ({assignment.candidates?.length || 0})
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {assignment.candidates?.map((product) => (
                                  <div
                                    key={product.asin}
                                    className="border rounded-lg p-4 hover:shadow-md transition-shadow flex flex-col"
                                  >
                                    {product.image?.url && (
                                      <img
                                        src={product.image.url}
                                        alt={product.title}
                                        className="w-full h-32 object-contain mb-3"
                                      />
                                    )}
                                    <h5 className="font-medium text-sm line-clamp-2 flex-1">
                                      {product.title}
                                    </h5>
                                    <div className="mt-2 space-y-1">
                                      {product.price?.displayAmount && (
                                        <p className="text-lg font-bold text-green-700">
                                          {product.price.displayAmount}
                                        </p>
                                      )}
                                      <div className="flex items-center gap-2 text-xs text-gray-500">
                                        {product.rating && (
                                          <span>⭐ {product.rating.toFixed(1)}</span>
                                        )}
                                        {product.reviewCount && (
                                          <span>({product.reviewCount} reviews)</span>
                                        )}
                                        {product.isPrime && (
                                          <span className="text-blue-600 font-medium">Prime</span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <span className={`px-2 py-0.5 rounded text-xs ${
                                          product.priority === 'essential' ? 'bg-red-100 text-red-800' :
                                          product.priority === 'recommended' ? 'bg-yellow-100 text-yellow-800' :
                                          'bg-gray-100 text-gray-800'
                                        }`}>
                                          {product.priority}
                                        </span>
                                        <span className="text-xs text-gray-400">
                                          {product.productType}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="mt-3 flex gap-2">
                                      <a
                                        href={product.affiliateUrl || product.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 text-center"
                                      >
                                        View ↗
                                      </a>
                                      <button
                                        onClick={() => handleAcceptProductCandidate(assignment, product)}
                                        disabled={acceptingProduct === `${assignment.activityId}-${product.asin}`}
                                        className="flex-1 px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                                      >
                                        {acceptingProduct === `${assignment.activityId}-${product.asin}` ? '...' : 'Add'}
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Video Preview Modal */}
          {previewVideo && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
              onClick={() => setPreviewVideo(null)}
            >
              <div 
                className="bg-black rounded-lg overflow-hidden max-w-4xl w-full mx-4"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="aspect-video">
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${previewVideo.videoId}?autoplay=1`}
                    title={previewVideo.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <div className="p-4 bg-gray-900 text-white">
                  <h3 className="font-semibold">{previewVideo.title}</h3>
                  <p className="text-sm text-gray-400">{previewVideo.channel}</p>
                  <button
                    onClick={() => setPreviewVideo(null)}
                    className="mt-3 px-4 py-2 border border-gray-600 rounded hover:bg-gray-800"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </AppLayout>
    </RoleGate>
  );
};

export default AdminResourcesDashboard;






